// Guards React's DOM reconciliation against in-browser translation engines
// (Google Translate, Chrome/Edge/Safari auto-translate, etc.).
//
// Those engines re-parent and replace text nodes behind React's back — wrapping
// translated text in nested <font> tags. When React later runs removeChild or
// insertBefore against a node whose parent has since been changed by the
// translator, the browser throws:
//
//   NotFoundError: The object can not be found here.
//
// Inside React's synchronous commit / error-recovery path this cascades into a
// hard "RangeError: Maximum call stack size exceeded" that takes down the whole
// page (production Sentry issue DRIVESHARE_V2-9S, seen on /search and
// /cars/:id for auto-translating nb-no / en-gb users).
//
// The established fix (facebook/react#11538) is to make removeChild and
// insertBefore no-op gracefully when the reference node's parent no longer
// matches, instead of throwing. The normal path (parent matches) is left
// completely unchanged, so non-translated sessions behave identically.

const GUARD_FLAG = "__bacTranslationDomGuard__";

export function installTranslationDomGuard(): void {
  if (typeof Node === "undefined" || !Node.prototype) return;

  const proto = Node.prototype as unknown as Record<string, unknown>;
  // Idempotent: never double-wrap (e.g. HMR, repeated boot).
  if (proto[GUARD_FLAG]) return;
  proto[GUARD_FLAG] = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    // The translator moved `child` under a different parent — React's own tree
    // no longer matches the live DOM. Removing it here would throw NotFoundError,
    // so skip the native call and hand the node back unchanged.
    if (child.parentNode !== this) {
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    // The reference node was re-parented by the translator; inserting relative
    // to it would throw NotFoundError. Return the new node without inserting so
    // React's commit phase can continue instead of crashing.
    if (referenceNode && referenceNode.parentNode !== this) {
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
